import { useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, UserX, Clock, CheckCircle2, CalendarPlus, X, Sun, Sunset, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AM_LOCATIONS = ["Car Drop Off"];
const PM_LOCATIONS = ["Main Tent", "Hockey Rink", "Auditorium", "After Care", "Front Door", "Classroom", "Overnight", "Broadway"];

// Local YYYY-MM-DD (avoids UTC shift from toISOString)
const _today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Office-facing widget — sits at the top of Admin & Supervisor dashboards.
 * Lets staff instantly mark a camper Absent / Late / Present for today when
 * a parent calls. Writes to /api/admin/quick-attendance which updates
 * app_status — the bus app picks this up within ~15s.
 *
 * Multi-date support — admin can tack on extra future dates (parent calling
 * about a vacation week). Day-of, those future-dated docs behave identically
 * to a same-day mark.
 */
export default function QuickAttendanceWidget({ campers = [] }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // {id, first_name, last_name, group_code}
  const [submitting, setSubmitting] = useState(null);
  const [dates, setDates] = useState([_today()]);
  const [showDateEditor, setShowDateEditor] = useState(false);
  // Date mode: "list" = pick individual dates; "range" = start..end auto-expanded
  // to every weekday in between (camp days only).
  const [dateMode, setDateMode] = useState("list");
  const [rangeStart, setRangeStart] = useState(_today());
  const [rangeEnd, setRangeEnd] = useState(_today());
  const [busOnly, setBusOnly] = useState(false);
  const [excPeriod, setExcPeriod] = useState(null); // 'am' | 'pm' | null
  const [excLocation, setExcLocation] = useState("");
  const [excNote, setExcNote] = useState("");
  // Early-dismissal call-in (parent on phone): hits /api/attendance/dismiss,
  // same path the rrdcattendance.com app uses — instantly fires the SMS +
  // notification banner to the head counselor.
  const [showDismissal, setShowDismissal] = useState(false);
  const [dismissalTime, setDismissalTime] = useState("");
  const [pickupBy, setPickupBy] = useState("");
  const inputRef = useRef(null);

  // Filter as you type — max 8 suggestions
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || selected) return [];
    return (campers || [])
      .filter(c => {
        const full = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
        return full.includes(q) || (c.group_code || "").toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [query, campers, selected]);

  const pick = (camper) => {
    setSelected(camper);
    setQuery(`${camper.first_name} ${camper.last_name}`);
  };

  const reset = () => {
    setSelected(null);
    setQuery("");
    setSubmitting(null);
    setDates([_today()]);
    setShowDateEditor(false);
    setDateMode("list");
    setRangeStart(_today());
    setRangeEnd(_today());
    setBusOnly(false);
    setExcPeriod(null);
    setExcLocation("");
    setExcNote("");
    setShowDismissal(false);
    setDismissalTime("");
    setPickupBy("");
    if (inputRef.current) inputRef.current.focus();
  };

  const addDate = () => setDates(d => [...d, ""]);
  const removeDate = (idx) => setDates(d => d.length > 1 ? d.filter((_, i) => i !== idx) : d);
  const updateDate = (idx, val) => setDates(d => d.map((x, i) => i === idx ? val : x));

  const cleanDates = useMemo(() => {
    if (dateMode === "range") {
      // Expand the range to every weekday between start and end (camp days
      // are Mon-Fri). Caps at 90 days to mirror the backend limit.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(rangeStart) || !/^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)) return [];
      if (rangeStart > rangeEnd) return [];
      const out = [];
      const start = new Date(rangeStart + "T12:00:00");
      const end = new Date(rangeEnd + "T12:00:00");
      const ms = (end - start) / (1000 * 60 * 60 * 24);
      if (ms > 90) return [];
      const cur = new Date(start);
      while (cur <= end) {
        const dow = cur.getDay(); // 0 = Sun, 6 = Sat
        if (dow !== 0 && dow !== 6) {
          const y = cur.getFullYear();
          const m = String(cur.getMonth() + 1).padStart(2, "0");
          const dd = String(cur.getDate()).padStart(2, "0");
          out.push(`${y}-${m}-${dd}`);
        }
        cur.setDate(cur.getDate() + 1);
      }
      return out;
    }
    const out = [];
    const seen = new Set();
    for (const d of dates) {
      const v = (d || "").trim();
      if (v && /^\d{4}-\d{2}-\d{2}$/.test(v) && !seen.has(v)) {
        seen.add(v); out.push(v);
      }
    }
    return out.sort();
  }, [dates, dateMode, rangeStart, rangeEnd]);

  const hasFutureDate = useMemo(() => {
    const t = _today();
    return cleanDates.some(d => d > t);
  }, [cleanDates]);

  // Early-dismissal call-in — POST to /api/attendance/dismiss exactly as the
  // external rrdcattendance.com app does. This triggers the same notification
  // banner + SMS to the head counselor as a dismissal from the bus iPad.
  const submitDismissal = async () => {
    if (!selected) {
      toast.error("Pick a camper first");
      return;
    }
    if (!dismissalTime.trim()) {
      toast.error("Enter the pickup time");
      return;
    }
    if (!cleanDates.length) {
      toast.error("Pick at least one date");
      return;
    }
    setSubmitting("dismiss");
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const base = {
        camperId: selected.id,
        camperName: `${selected.first_name} ${selected.last_name}`.trim(),
        groupCode: selected.group_code,
        dismissalTime: dismissalTime.trim(),
        pickupBy: pickupBy.trim(),
        source: "admin_widget",
      };
      const today = _today();
      let smsSent = false;
      let futureCount = 0;
      for (const d of cleanDates) {
        const res = await axios.post(`${API}/attendance/dismiss`, { ...base, date: d }, { headers });
        if (res.data?.sms_sent) smsSent = true;
        if (d > today) futureCount += 1;
      }
      const n = cleanDates.length;
      const parts = [`${selected.first_name} ${selected.last_name} — early dismissal at ${dismissalTime} logged on ${n} date${n !== 1 ? "s" : ""}`];
      if (pickupBy) parts.push(`pickup: ${pickupBy}`);
      if (smsSent) parts.push("📱 SMS sent to counselor");
      if (futureCount) parts.push(`${futureCount} future date${futureCount !== 1 ? "s" : ""} planned — counselor is texted the morning of each day`);
      toast.success(parts.join(". "));
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log early dismissal");
      setSubmitting(null);
    }
  };

  const submit = async (status) => {
    if (!selected) {
      toast.error("Pick a camper first");
      return;
    }
    if (!cleanDates.length) {
      toast.error("Pick at least one date");
      return;
    }
    setSubmitting(status);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // BUS-ONLY MODE — instead of marking absent in the attendance app,
      // write an AM (Car Drop Off) and PM (Front Door) dismissal exception
      // for each date so the kid is pulled off both buses but stays in
      // the attendance roster as present. Only applies for Absent/Late
      // — Present means clear, so bus-only doesn't make sense there.
      //
      // Range mode: one API call per period with start_date/end_date — the
      // backend expands to CAMP_DAYS (skips weekends). Mirrors the camper
      // card's range save so behavior is identical.
      if (busOnly && (status === "absent" || status === "late")) {
        if (dateMode === "range") {
          const amRes = await axios.put(
            `${API}/admin/dismissal-exceptions/${selected.id}`,
            { location: "Car Drop Off", note: "Bus-only — parent transport", start_date: rangeStart, end_date: rangeEnd, period: "am" },
            { headers }
          );
          const pmRes = await axios.put(
            `${API}/admin/dismissal-exceptions/${selected.id}`,
            { location: "Front Door", note: "Bus-only — parent pickup", start_date: rangeStart, end_date: rangeEnd, period: "pm" },
            { headers }
          );
          const n = (amRes.data?.dates || []).length || cleanDates.length;
          const futureNote = hasFutureDate ? " (planned)" : "";
          toast.success(`${selected.first_name} ${selected.last_name} pulled from bus on ${n} date${n !== 1 ? "s" : ""}${futureNote}. Still present in attendance app.`);
          reset();
          return;
        }
        let okCount = 0;
        for (const d of cleanDates) {
          await axios.put(
            `${API}/admin/dismissal-exceptions/${selected.id}`,
            { location: "Car Drop Off", note: "Bus-only — parent transport", date: d, period: "am" },
            { headers }
          );
          await axios.put(
            `${API}/admin/dismissal-exceptions/${selected.id}`,
            { location: "Front Door", note: "Bus-only — parent pickup", date: d, period: "pm" },
            { headers }
          );
          okCount += 1;
        }
        const futureNote = hasFutureDate ? " (planned)" : "";
        toast.success(`${selected.first_name} ${selected.last_name} pulled from bus on ${okCount} date${okCount !== 1 ? "s" : ""}${futureNote}. Still present in attendance app.`);
        reset();
        return;
      }

      // STANDARD MODE — write to app_status via quick-attendance
      const res = await axios.post(
        `${API}/admin/quick-attendance`,
        { camper_id: selected.id, status, dates: cleanDates },
        { headers }
      );
      const verb = status === "present" ? "marked Present (cleared)" : status === "late" ? "marked Late" : "marked Absent";
      const count = res.data?.count || cleanDates.length;
      const futureNote = hasFutureDate ? " (planned — applies on those dates)" : ". Bus app will refresh in ~15s.";
      toast.success(`${selected.first_name} ${selected.last_name} ${verb} on ${count} date${count !== 1 ? "s" : ""}${futureNote}`);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update attendance");
      setSubmitting(null);
    }
  };

  // Save an AM or PM dismissal exception for the selected camper across all
  // chosen dates. Uses the same /admin/dismissal-exceptions PUT endpoint as
  // the per-camper editor on the camper card.
  const saveException = async () => {
    if (!selected) {
      toast.error("Pick a camper first");
      return;
    }
    if (!cleanDates.length) {
      toast.error("Pick at least one date");
      return;
    }
    if (!excPeriod) {
      toast.error("Pick AM or PM first");
      return;
    }
    if (!excLocation) {
      toast.error("Pick a location");
      return;
    }
    setSubmitting(`exc-${excPeriod}`);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Range mode → single API call (server expands to CAMP_DAYS). Mirrors
      // the camper-card range save EXACTLY, so behavior is identical.
      if (dateMode === "range") {
        const res = await axios.put(
          `${API}/admin/dismissal-exceptions/${selected.id}`,
          { location: excLocation, note: excNote, start_date: rangeStart, end_date: rangeEnd, period: excPeriod },
          { headers }
        );
        const n = (res.data?.dates || []).length || cleanDates.length;
        const futureNote = hasFutureDate ? " (planned)" : "";
        toast.success(`${selected.first_name} ${selected.last_name} — ${excPeriod.toUpperCase()} exception (${excLocation}) saved on ${n} date${n !== 1 ? "s" : ""}${futureNote}`);
        reset();
        return;
      }

      // List mode → one PUT per chosen date (matches camper-card single-day save)
      let okCount = 0;
      for (const d of cleanDates) {
        await axios.put(
          `${API}/admin/dismissal-exceptions/${selected.id}`,
          { location: excLocation, note: excNote, date: d, period: excPeriod },
          { headers }
        );
        okCount += 1;
      }
      const futureNote = hasFutureDate ? " (planned)" : "";
      toast.success(`${selected.first_name} ${selected.last_name} — ${excPeriod.toUpperCase()} exception (${excLocation}) saved on ${okCount} date${okCount !== 1 ? "s" : ""}${futureNote}`);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save exception");
      setSubmitting(null);
    }
  };

  return (
    <Card
      className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm"
      data-testid="quick-attendance-widget"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <UserX className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <h3 className="font-heading font-extrabold text-base sm:text-lg text-amber-900 tracking-tight">
            Mark Absent / Late
          </h3>
          <span className="ml-auto text-[10px] sm:text-xs text-amber-800/80 font-medium">
            Office quick action — syncs to bus in ~15s
          </span>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-700" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder="Type camper name to search…"
            className="pl-9 pr-20 bg-white border-amber-300 focus-visible:ring-amber-500"
            data-testid="quick-attendance-search"
            autoComplete="off"
          />
          {(query || selected) && (
            <button
              onClick={reset}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-amber-700 hover:text-amber-900 font-semibold"
              data-testid="quick-attendance-clear"
              type="button"
            >
              Clear
            </button>
          )}

          {suggestions.length > 0 && (
            <div
              className="absolute z-30 mt-1 w-full bg-white border border-amber-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
              data-testid="quick-attendance-suggestions"
            >
              {suggestions.map((c) => (
                <button
                  key={c.id}
                  onClick={() => pick(c)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-amber-50 border-b border-amber-100 last:border-0"
                  data-testid={`quick-attendance-suggestion-${c.id}`}
                  type="button"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {c.first_name} {c.last_name}
                  </span>
                  <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                    {c.group_code || "—"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div
            className="flex items-center gap-2 px-3 py-2 bg-white border border-amber-200 rounded-lg"
            data-testid="quick-attendance-selected"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-gray-900">
              {selected.first_name} {selected.last_name}
            </span>
            <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
              {selected.group_code || "—"}
            </span>
          </div>
        )}

        {/* Dates row — defaults to today. Admin can add more dates so a
            parent calling about a vacation week is handled in one shot. */}
        {selected && (
          <div className="bg-white border border-amber-200 rounded-lg p-2.5 space-y-1.5"
            data-testid="quick-attendance-dates">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-amber-900 uppercase">
                Date{cleanDates.length > 1 ? `s · ${cleanDates.length}` : ""}
                {hasFutureDate && (
                  <span className="ml-1.5 inline-block text-[9px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                    PLANNED
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setShowDateEditor(v => !v)}
                className="text-[11px] text-amber-700 hover:text-amber-900 font-semibold"
                data-testid="quick-attendance-toggle-dates"
              >
                {showDateEditor ? "Hide" : "Edit dates"}
              </button>
            </div>
            {!showDateEditor ? (
              <div className="text-xs text-gray-700 flex flex-wrap gap-1">
                {cleanDates.length === 0
                  ? <span className="italic text-gray-400">no dates selected</span>
                  : cleanDates.map(d => (
                    <span key={d} className="bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{d}</span>
                  ))}
              </div>
            ) : (
              <div className="space-y-2">
                {/* Mode toggle */}
                <div className="flex gap-1.5" data-testid="quick-attendance-date-mode">
                  <button
                    type="button"
                    onClick={() => setDateMode("list")}
                    className={`flex-1 text-[11px] font-bold uppercase px-2 py-1 rounded border ${
                      dateMode === "list"
                        ? "bg-amber-600 text-white border-amber-700"
                        : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                    }`}
                    data-testid="quick-attendance-date-mode-list"
                  >
                    Specific dates
                  </button>
                  <button
                    type="button"
                    onClick={() => setDateMode("range")}
                    className={`flex-1 text-[11px] font-bold uppercase px-2 py-1 rounded border ${
                      dateMode === "range"
                        ? "bg-amber-600 text-white border-amber-700"
                        : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
                    }`}
                    data-testid="quick-attendance-date-mode-range"
                  >
                    Range
                  </button>
                </div>

                {dateMode === "range" ? (
                  <div className="space-y-1.5">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] font-bold text-amber-700 uppercase mb-0.5">Start</div>
                        <input
                          type="date"
                          value={rangeStart}
                          onChange={(e) => setRangeStart(e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          data-testid="quick-attendance-range-start"
                        />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-amber-700 uppercase mb-0.5">End</div>
                        <input
                          type="date"
                          value={rangeEnd}
                          onChange={(e) => setRangeEnd(e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          data-testid="quick-attendance-range-end"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-amber-700/90 italic">
                      Weekdays only (camp days). {cleanDates.length} date{cleanDates.length !== 1 ? "s" : ""} will be marked.
                      {cleanDates.length === 0 && rangeStart && rangeEnd && rangeStart > rangeEnd && (
                        <span className="text-red-600"> Start must be before end.</span>
                      )}
                    </p>
                  </div>
                ) : (
                  <>
                    {dates.map((d, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="date"
                          value={d}
                          onChange={(e) => updateDate(idx, e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                          data-testid={`quick-attendance-date-${idx}`}
                        />
                        {dates.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDate(idx)}
                            className="text-red-600 hover:text-red-800 p-1"
                            data-testid={`quick-attendance-date-remove-${idx}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addDate}
                      className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-semibold mt-1"
                      data-testid="quick-attendance-add-date"
                    >
                      <CalendarPlus className="w-3.5 h-3.5" />
                      Add another date
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Button
            disabled={!selected || submitting !== null}
            onClick={() => submit("absent")}
            className="bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-40"
            data-testid="quick-attendance-absent-btn"
          >
            <UserX className="w-4 h-4 mr-1" />
            {submitting === "absent" ? "…" : "Absent"}
          </Button>
          <Button
            disabled={!selected || submitting !== null}
            onClick={() => submit("late")}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold disabled:opacity-40"
            data-testid="quick-attendance-late-btn"
          >
            <Clock className="w-4 h-4 mr-1" />
            {submitting === "late" ? "…" : "Late"}
          </Button>
          <Button
            disabled={!selected || submitting !== null}
            onClick={() => submit("present")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40"
            data-testid="quick-attendance-present-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {submitting === "present" ? "…" : "Present"}
          </Button>
        </div>

        {/* Bus-only toggle — only meaningful for Absent / Late marks. When
            checked, the kid is pulled off BOTH buses (AM Car Drop Off + PM
            Front Door dismissal exceptions) but stays present in the
            attendance app. */}
        {selected && (
          <label
            className="flex items-center gap-2 px-2 py-1.5 bg-white/70 border border-amber-200 rounded text-xs text-amber-900 cursor-pointer select-none"
            data-testid="quick-attendance-bus-only-row"
          >
            <input
              type="checkbox"
              checked={busOnly}
              onChange={(e) => setBusOnly(e.target.checked)}
              className="w-4 h-4 accent-amber-600"
              data-testid="quick-attendance-bus-only"
            />
            <span className="font-semibold">Bus only</span>
            <span className="text-[10px] text-amber-700/80">
              — pulls off both buses, kid stays present in attendance app
            </span>
          </label>
        )}

        {/* AM / PM Exception controls — sets a dismissal exception on the
            chosen dates (same flow as the per-camper editor on the camper
            card, but in one shared widget). */}
        {selected && (
          <div className="bg-white border border-rose-200 rounded-lg p-2.5 space-y-2"
            data-testid="quick-attendance-exception-block">
            <div className="grid grid-cols-2 gap-2">
              <Button
                disabled={submitting !== null}
                onClick={() => { setExcPeriod(p => p === "am" ? null : "am"); setExcLocation(""); setExcNote(""); }}
                className={`${excPeriod === "am" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-white border-2 border-rose-300 text-rose-700 hover:bg-rose-50"} font-bold disabled:opacity-40`}
                data-testid="quick-attendance-am-exc-btn"
              >
                <Sun className="w-4 h-4 mr-1" />
                AM Exception
              </Button>
              <Button
                disabled={submitting !== null}
                onClick={() => { setExcPeriod(p => p === "pm" ? null : "pm"); setExcLocation(""); setExcNote(""); }}
                className={`${excPeriod === "pm" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-white border-2 border-rose-300 text-rose-700 hover:bg-rose-50"} font-bold disabled:opacity-40`}
                data-testid="quick-attendance-pm-exc-btn"
              >
                <Sunset className="w-4 h-4 mr-1" />
                PM Exception
              </Button>
            </div>
            {excPeriod && (
              <div className="space-y-1.5">
                <div>
                  <div className="text-[10px] font-bold text-rose-700 uppercase mb-1">
                    {excPeriod.toUpperCase()} Drop-off / Pickup Location
                  </div>
                  <select
                    value={excLocation}
                    onChange={(e) => setExcLocation(e.target.value)}
                    className="w-full border border-rose-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="quick-attendance-exc-location"
                  >
                    <option value="">Pick a location…</option>
                    {(excPeriod === "am" ? AM_LOCATIONS : PM_LOCATIONS).map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={excNote}
                  onChange={(e) => setExcNote(e.target.value)}
                  placeholder="Optional note (parent name, time, etc.)"
                  className="w-full border border-rose-200 rounded px-2 py-1.5 text-xs"
                  data-testid="quick-attendance-exc-note"
                />
                <Button
                  disabled={!excLocation || submitting !== null}
                  onClick={saveException}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold disabled:opacity-40"
                  data-testid="quick-attendance-exc-save"
                >
                  {submitting === `exc-${excPeriod}` ? "…" : `Save ${excPeriod.toUpperCase()} Exception`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Early Dismissal call-in — parent on phone says "I'm coming to get
            them at 1:30". One submit triggers the SAME flow as the bus iPad
            app: attendance flipped to dismissed, banner posted, SMS fired to
            the head counselor. */}
        {selected && (
          <div className="bg-white border border-orange-200 rounded-lg p-2.5 space-y-2"
            data-testid="quick-attendance-dismissal-block">
            <Button
              disabled={submitting !== null}
              onClick={() => { setShowDismissal(v => !v); setDismissalTime(""); setPickupBy(""); }}
              className={`${showDismissal ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-white border-2 border-orange-300 text-orange-700 hover:bg-orange-50"} w-full font-bold disabled:opacity-40`}
              data-testid="quick-attendance-dismissal-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              {showDismissal ? "Cancel — Don't dismiss" : "Early Dismissal (parent on phone)"}
            </Button>
            {showDismissal && (
              <div className="space-y-1.5">
                <div>
                  <div className="text-[10px] font-bold text-orange-700 uppercase mb-1">Pickup time</div>
                  <input
                    type="text"
                    value={dismissalTime}
                    onChange={(e) => setDismissalTime(e.target.value)}
                    placeholder="e.g. 1:30 PM"
                    className="w-full border border-orange-300 rounded px-2 py-1.5 text-sm"
                    data-testid="quick-attendance-dismissal-time"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-orange-700 uppercase mb-1">Picked up by</div>
                  <input
                    type="text"
                    value={pickupBy}
                    onChange={(e) => setPickupBy(e.target.value)}
                    placeholder="e.g. Mom – Sarah Smith, Grandpa"
                    className="w-full border border-orange-300 rounded px-2 py-1.5 text-sm"
                    data-testid="quick-attendance-dismissal-pickup"
                  />
                </div>
                <Button
                  disabled={!dismissalTime.trim() || submitting !== null}
                  onClick={submitDismissal}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold disabled:opacity-40"
                  data-testid="quick-attendance-dismissal-save"
                >
                  {submitting === "dismiss" ? "…" : cleanDates.length > 1
                    ? `Log Dismissal on ${cleanDates.length} dates`
                    : "Send to Counselor + Log Dismissal"}
                </Button>
                <p className="text-[10px] text-orange-700/90 italic">
                  Uses the dates picked above — future dates are saved as planned dismissals (shown on the dashboard; counselor texted the morning of each day).
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
